import createClient, { type Middleware } from "openapi-fetch";

import { config } from "@/config";
import { REQUESTED_WITH } from "@/core/constants";

import { ENDPOINTS } from "./endpoints";
import { ApiError } from "./errors";
import type { paths } from "./schema";

type Unauthorized = () => void;
const unauthorizedListeners = new Set<Unauthorized>();

/** The session ended (401 on a call): the session layer listens and shows the sign-in screen. */
export function onUnauthorized(listener: Unauthorized): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

/** Calls whose 401 means "not signed in" (asked on purpose), not "the session just ended". */
const SESSION_PATHS: readonly string[] = [ENDPOINTS.me, ENDPOINTS.session];

const notifyOnUnauthorized: Middleware = {
  onResponse({ response, request }) {
    if (response.status === 401 && !SESSION_PATHS.includes(new URL(request.url).pathname)) {
      unauthorizedListeners.forEach((listener) => listener());
    }
    return undefined;
  },
};

/**
 * The one API client: typed from the backend's OpenAPI (src/core/api/schema.d.ts, `npm run api:types`),
 * sends the HttpOnly session cookie and the CSRF header on every call. Screens never call fetch directly.
 */
export const api = createClient<paths>({
  // No API URL configured (local dev): the API is on this origin, behind Vite's proxy.
  baseUrl: config.apiBaseUrl || window.location.origin,
  credentials: "include",
  // Never answer from the browser's HTTP cache: account and money data must be fresh, and a bad cached
  // response (e.g. an HTML page during a broken deploy) must not stick for its max-age.
  cache: "no-store",
  headers: { [REQUESTED_WITH.header]: REQUESTED_WITH.value },
  // Looked up per call (not captured once), so instrumentation and tests see every request.
  fetch: (request) => globalThis.fetch(request),
});
api.use(notifyOnUnauthorized);

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/** Awaits an `api.GET/POST/…` call: its body on success, an ApiError otherwise (network failures too). */
export async function call<T>(request: Promise<FetchResult<T>>): Promise<T> {
  let result: FetchResult<T>;
  try {
    result = await request;
  } catch (cause) {
    throw ApiError.network(cause);
  }
  if (!result.response.ok) throw ApiError.fromResponse(result.response.status, result.error);
  return result.data as T;
}

/** A fresh key per money-changing request (API.md §1); reuse it only to retry that same request. */
export function idempotencyKey(): string {
  return `admin-web-${crypto.randomUUID()}`;
}
